import { DateFormat, TimeFormat } from '@treneo/shared/constants';
import { logger } from '@treneo/shared/logger';

import { convertDateToIsoString } from './date-utils';
import {
  convertUtcToTimezone,
  createDayjsInstance,
  formatDayjsToString,
  getLocalTimezone,
  isValidDayjs
} from './dayjs-wrapper';

export const convertDateFormatToDayjs = (dateFormat: DateFormat): string => {
  switch (dateFormat) {
    case DateFormat.YYYY_MM_DD:
      return 'YYYY-MM-DD';
    case DateFormat.MM_DD_YYYY:
      return 'MM-DD-YYYY';
    case DateFormat.DD_MM_YYYY:
      return 'DD-MM-YYYY';
    default:
      return 'YYYY-MM-DD';
  }
};

export const convertTimeFormatToDayjs = (timeFormat: TimeFormat): string => {
  switch (timeFormat) {
    case TimeFormat.H24:
      return 'HH:mm';
    case TimeFormat.H12:
      return 'hh:mm A';
    default:
      return 'HH:mm';
  }
};

export const convertDayjsToDisplayFormat = (dayjsFormat: string): string => dayjsFormat;

export const convertTimeToFormat = (timeString: string, timeFormat: TimeFormat): string => {
  if (!timeString) return timeString;

  try {
    const time = createDayjsInstance(timeString);
    if (!time.isValid()) return timeString;

    const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);
    return time.format(dayjsTimeFormat);
  } catch (error) {
    logger.warn('Failed to convert time to format:', { timeString, timeFormat }, error);
    return timeString;
  }
};

export const formatDate = (date: Date, dateFormat: DateFormat): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  switch (dateFormat) {
    case DateFormat.YYYY_MM_DD:
      return `${year}-${month}-${day}`;
    case DateFormat.MM_DD_YYYY:
      return `${month}-${day}-${year}`;
    case DateFormat.DD_MM_YYYY:
      return `${day}-${month}-${year}`;
    default:
      return `${year}-${month}-${day}`;
  }
};

export const formatDateForInput = (date: string): string => {
  if (!date) return '';

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toISOString().split('T')[0];
};

export const formatTimeForInput = (time: string): string => {
  if (!time) return '';

  const timeRegex = /^(\d{1,2}):(\d{2})/;
  const timeMatch = timeRegex.exec(time);
  if (!timeMatch) return '';

  const [, hours, minutes] = timeMatch;
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
};

export const createCombinedDateTimeValue = (date: string, time: string): string => {
  if (date && time) {
    const formattedDate = formatDateForInput(date);
    const formattedTime = formatTimeForInput(time);
    return `${formattedDate}T${formattedTime}`;
  }
  return '';
};

export const parseCombinedDateTimeValue = (
  datetimeLocal: string
): { date: string; time: string } => {
  if (!datetimeLocal) return { date: '', time: '' };

  const [date, time] = datetimeLocal.split('T');
  return {
    date: formatDateForInput(date || ''),
    time: formatTimeForInput(time || '')
  };
};

export const getCurrentDateIso = (): string => new Date().toISOString().split('T')[0];

export const getDateLimits = () => {
  const currentDate = getCurrentDateIso();
  const maxDate = new Date(new Date().getFullYear() + 5, 11, 31).toISOString().split('T')[0];
  return {
    minDate: currentDate,
    maxDate
  };
};

export const createIsoDateTime = (date: string, time: string): string => `${date}T${time}:00`;

export const parseIsoDateTime = (isoString: string): { date: string; time: string } | null => {
  if (!isoString?.includes('T')) return null;

  const [datePart, timePart] = isoString.split('T');
  if (!datePart || !timePart) return null;

  return {
    date: datePart,
    time: timePart.slice(0, 5)
  };
};

export const addHoursToTime = (timeString: string, hours: number): string => {
  if (!isValidTimeFormat(timeString)) return timeString;

  const [hoursStr, minutesStr] = timeString.split(':');
  const currentHours = parseInt(hoursStr, 10);
  const currentMinutes = parseInt(minutesStr, 10);
  const date = new Date();
  date.setHours(currentHours, currentMinutes, 0, 0);
  date.setHours(date.getHours() + hours);
  return date.toTimeString().slice(0, 5);
};

export const getDifferenceTime = (firstDate: Date, secondDate: Date): number =>
  createDayjsInstance(secondDate).diff(createDayjsInstance(firstDate));

export const generateTempId = (): string =>
  `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const isValidTimeFormat = (timeString: string): boolean => {
  const regex = /^([01]?\d|2[0-3]):[0-5]\d$/;
  return regex.test(timeString);
};

export const formatDateWithFormat = (
  date: string | Date | null | undefined,
  dateFormat: DateFormat,
  fallback = ''
): string => {
  const iso = convertDateToIsoString(date);
  if (!iso) {
    return fallback;
  }
  const timezone = getLocalTimezone();
  const dayjsDate = convertUtcToTimezone(iso, timezone);
  if (!isValidDayjs(dayjsDate)) {
    return fallback;
  }
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  return formatDayjsToString(dayjsDate, dayjsDateFormat) ?? fallback;
};

export const formatDateTimeWithFormat = (
  date: string | Date | null | undefined,
  dateFormat: DateFormat,
  timeFormat: TimeFormat,
  fallback = ''
): string => {
  const iso = convertDateToIsoString(date);
  if (!iso) {
    return fallback;
  }
  const timezone = getLocalTimezone();
  const dayjsDate = convertUtcToTimezone(iso, timezone);
  if (!isValidDayjs(dayjsDate)) {
    return fallback;
  }
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);
  const combinedFormat = `${dayjsDateFormat} ${dayjsTimeFormat}`;
  return formatDayjsToString(dayjsDate, combinedFormat) ?? fallback;
};

export const formatDateLocale = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '';
  }
  try {
    return new Date(date).toLocaleDateString();
  } catch {
    return '';
  }
};

export const formatDateTimeLocale = (date: string | Date | null | undefined): string => {
  if (!date) {
    return '';
  }
  try {
    return new Date(date).toLocaleString();
  } catch {
    return '';
  }
};

export const formatDateLocaleWithFallback = (
  date: string | Date | null | undefined,
  fallback: string
): string => {
  if (!date) {
    return fallback;
  }
  try {
    return new Date(date).toLocaleDateString();
  } catch {
    return fallback;
  }
};

export const formatDateTimeLocaleWithFallback = (
  date: string | Date | null | undefined,
  fallback: string
): string => {
  if (!date) {
    return fallback;
  }
  try {
    return new Date(date).toLocaleString();
  } catch {
    return fallback;
  }
};
